import OfflineLogger from '@/utils/offlineLogger';

/**
 * Lock globale di sincronizzazione condiviso tra tutti i trigger
 * (SmartAutoSyncService, SyncService periodico/manuale, OfflineProvider).
 *
 * Senza questo lock ogni servizio aveva il proprio flag `syncInProgress`
 * locale: due cicli potevano quindi drenare `offlineDb.syncQueue` in
 * parallelo, causando doppie chiamate Supabase, possibili duplicati e un
 * contatore `pendingOperations` incoerente.
 *
 * Semantica: se un ciclo è già in corso, i trigger concorrenti si
 * agganciano alla stessa promise in volo (attendono) invece di avviarne
 * uno nuovo → una sola esecuzione effettiva alla volta.
 */
class SyncLock {
  private inFlight: Promise<unknown> | null = null;

  /** True se un ciclo di sync è attualmente in esecuzione. */
  get isSyncing(): boolean {
    return this.inFlight !== null;
  }

  /**
   * Esegue `task` garantendo che un solo ciclo di sync sia attivo alla volta.
   * I trigger concorrenti ricevono la promise del ciclo già in corso senza
   * eseguire `task` una seconda volta.
   */
  async runExclusive<T>(task: () => Promise<T>): Promise<T> {
    if (this.inFlight) {
      OfflineLogger.info('SyncLock', 'Sync già in corso: il trigger si aggancia al ciclo attivo');
      return this.inFlight as Promise<T>;
    }

    const run = (async () => {
      try {
        return await task();
      } finally {
        this.inFlight = null;
      }
    })();

    this.inFlight = run;
    return run;
  }
}

export const syncLock = new SyncLock();
